import React, { useRef, useCallback, forwardRef, RefObject } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { AccessLevel } from '@bimdata/definitions/const';
import { accessLevelLabels } from '@/contracts';
import TextField, { TextFieldProps } from '@/components/TextField';
import InfoIcon from '@/components/icons/Info';
import Hint from './Hint';

type AccessSelectorProps = Omit<TextFieldProps, 'onChange' | 'error'> & {
  value: AccessLevel | null;
  onHelp?: () => void;
  onChange?: (e: AccessLevel) => void | Promise<void>;
  excludeLevels?: AccessLevel[];
  label?: string,
  error?: string,
  disabled?: boolean,
  sx?: object,
  hint?: string | null,
};

const AccessSelector = forwardRef((
    {
      value, onChange, excludeLevels = [], label, error, disabled, hint, onHelp, ...props
    }: AccessSelectorProps,
    ref: RefObject<HTMLDivElement>,
) => {
  const element = useRef<HTMLDivElement>(null);
  const hintShowCondition = useCallback(() => !!hint, [!!hint]);
  return <>
    <div ref={element} style={{ display: 'flex', flexDirection: 'row', gap: '0px' }}>
      <TextField
        ref={ref}
        {...props}
        select={!disabled}
        size="small"
        label={label}
        value={value ? (disabled ? accessLevelLabels(value) : value) : ''}
        hiddenLabel
        onChange={(e) => (!!onChange && !!e.target.value && onChange(e.target.value as AccessLevel))}
        {...(!!error
          ? {
            error: true,
            helperText: error
          }
          : {}
        )}
        disabled={disabled}
      >
        {!excludeLevels.includes(AccessLevel.READER) && (
          <MenuItem value={AccessLevel.READER}>
            {accessLevelLabels(AccessLevel.READER)}
          </MenuItem>
        )}
        {!excludeLevels.includes(AccessLevel.WRITER) && (
          <MenuItem value={AccessLevel.WRITER}>
            {accessLevelLabels(AccessLevel.WRITER)}
          </MenuItem>
        )}
        {!excludeLevels.includes(AccessLevel.ADMIN) && (
          <MenuItem value={AccessLevel.ADMIN}>
            {accessLevelLabels(AccessLevel.ADMIN)}
          </MenuItem>
        )}
        {!excludeLevels.includes(AccessLevel.OWNER) && (
          <MenuItem value={AccessLevel.OWNER}>
            {accessLevelLabels(AccessLevel.OWNER)}
          </MenuItem>
        )}
      </TextField>
      {!!onHelp && (<InfoIcon style={{ width: '32px', height: '32px', padding: '6px', color: '#6B778C', cursor: 'pointer' }} onClick={onHelp}/>)}
    </div>
    <Hint
      anchor={element}
      placement='bottom-start'
      delay={250}
      showCondition={hintShowCondition}
    >
      {hint}
    </Hint>
  </>;
});

export default AccessSelector;
